import { useState, type ReactNode } from 'react'
import { Building2, Check, ChevronRight, Download, History, KeyRound } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { daysBetween, paisaToRupees, todayIso } from '@cattle/money'

import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  FilterPills,
  LoadingSkeleton,
  Money,
  PageHeader,
  Pagination,
  QueryBoundary,
  RowAction,
  SearchInput,
  Section,
  StatusBadge,
  useSearchTerm,
  useToast,
  useUrlFilter,
  type Column,
  type StatusTone,
} from '../../../components'
import { useIsPlatformOwner } from '../../../app/session'
import {
  useBusinesses,
  useOverview,
  usePlans,
  type Business,
  type PlanCode,
} from '../../../fixtures'
import { downloadCsv } from '../../../lib/csv'
import { formatBytes, formatDateShort } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { routes } from '../../../lib/routes'
import { AddBusinessButton, AddBusinessDialog } from '../AddBusinessDialog'

/**
 * P-02 · Businesses.
 *
 * Book value is a column, because it is what says which of the 47 matter:
 * Rana Mandi carries Rs 1.46 crore of cattle and pays Rs 8,000. The status
 * column is always a WORD — "Ends today" and "11 days left" are statuses a
 * coloured dot could never carry.
 *
 * The add-business form is `AddBusinessDialog`, shared with P-01. It is the
 * only place the create request is built, so the two entry points cannot drift.
 */

const FILTERS = ['all', 'paying', 'trial', 'pastdue', 'suspended'] as const
type StatusFilter = (typeof FILTERS)[number]

const COUNT = new Intl.NumberFormat('en-IN')

/** "12 August 2026" → "Aug 2026". The deck dates a row by month, not by day. */
function sinceMonth(iso: string): string {
  return formatDateShort(iso).split(' ').slice(1).join(' ')
}

function matchesFilter(business: Business, filter: StatusFilter): boolean {
  switch (filter) {
    case 'paying':
      return business.status === 'ACTIVE'
    case 'trial':
      return business.status === 'TRIAL'
    case 'pastdue':
      return business.status === 'PAST_DUE'
    case 'suspended':
      return business.status === 'SUSPENDED'
    default:
      return true
  }
}

/** Word and tone together. A trial says how much of it is left. */
function statusOf(business: Business): { tone: StatusTone; label: string } {
  switch (business.status) {
    case 'ACTIVE':
      return { tone: 'ok', label: t('p.businesses.statusActive') }
    case 'PAST_DUE':
      return { tone: 'danger', label: t('p.businesses.statusPastDue') }
    case 'SUSPENDED':
      return { tone: 'neutral', label: t('p.businesses.statusSuspended') }
    // Dunning escalates here on its own, and the row used to fall through to
    // the trial branch below and be labelled "N days left" — a frozen book
    // described as a trial in progress. Since C10 it means what SUSPENDED
    // means; only the hand that froze it differs.
    case 'READ_ONLY':
      return { tone: 'neutral', label: t('p.businesses.statusReadOnly') }
    default: {
      const left = business.trialEndsOn ? daysBetween(todayIso(), business.trialEndsOn) : 0
      return {
        tone: 'warn',
        label:
          left <= 0
            ? t('p.businesses.statusEndsToday')
            : t('p.businesses.statusDaysLeft', { count: left }),
      }
    }
  }
}

/**
 * A rupee figure for a CSV cell.
 *
 * An ABSENT figure is an empty cell, never `0`. The server omits a `...Paisa`
 * key for an actor without `platform.billing` and under Price Lock; writing a
 * zero there would be this console making a claim about somebody's money that
 * nobody made to it.
 */
const rupees = (paisa: number | undefined): string =>
  paisa === undefined ? '' : String(paisaToRupees(paisa))

export function BusinessesScreen() {
  const businesses = useBusinesses()
  const overview = useOverview()
  const plans = usePlans()
  const isOwner = useIsPlatformOwner()
  const navigate = useNavigate()
  const toast = useToast()

  const [filter, setFilter] = useUrlFilter<StatusFilter>('status', 'all', FILTERS)
  const term = useSearchTerm()

  const [adding, setAdding] = useState(false)

  /** A plan name is the platform owner's own wording (P-05), so it is data. */
  const planName = (code: PlanCode): string =>
    plans.data?.find((plan) => plan.code === code)?.name ?? t(`p.plan.${code}`)

  const all = businesses.data ?? []
  const total = overview.data?.totalBusinesses ?? all.length

  // Filtered once, here, so the file and the table can never disagree about
  // which rows the reader is looking at.
  const shown = all.filter(
    (row) =>
      matchesFilter(row, filter) &&
      (term === '' ||
        row.name.toLowerCase().includes(term) ||
        row.ownerName.toLowerCase().includes(term) ||
        row.city.toLowerCase().includes(term)),
  )

  /*
    THE EXPORT.

    CONTRACT GAP: there is no `GET /platform/businesses/export`, and no
    endpoint anywhere that serves this list as a file. So it is written in the
    browser from the rows on screen — which means it is the FILTERED view, not
    the whole platform, and the toast says so rather than implying the server
    prepared a complete export.
  */
  const exportCsv = () => {
    if (shown.length === 0) {
      toast.show(t('p.businesses.exportEmpty'), 'info')
      return
    }

    downloadCsv(
      `${t('p.businesses.exportFilename')}-${todayIso()}`,
      [
        t('label.business'),
        t('label.owner'),
        t('p.businesses.cityLabel'),
        t('label.plan'),
        t('label.animals'),
        t('p.businesses.colBookValue'),
        t('label.storage'),
        t('label.status'),
        t('p.businesses.colPays'),
      ],
      shown.map((row) => [
        row.name,
        row.ownerName,
        row.city,
        planName(row.plan),
        row.animals,
        rupees(row.bookValuePaisa),
        formatBytes(row.storageMb),
        statusOf(row).label,
        rupees(row.paysPaisa),
      ]),
    )

    toast.show(t('p.businesses.exported', { count: shown.length }))
  }

  const counts = {
    all: total,
    paying: overview.data?.payingCount ?? all.filter((b) => b.status === 'ACTIVE').length,
    trial: overview.data?.trialCount ?? all.filter((b) => b.status === 'TRIAL').length,
    pastdue: overview.data?.pastDueCount ?? all.filter((b) => b.status === 'PAST_DUE').length,
    suspended: all.filter((b) => b.status === 'SUSPENDED').length,
  }

  const emptyState = (
    <EmptyState
      icon={<Building2 className="size-6" />}
      title={t('p.businesses.emptyTitle')}
      body={t('p.businesses.emptyBody')}
      /* Gated here too, so an account that cannot add a business is not left
         looking at the empty state's blank space where a button would be. */
      action={isOwner ? <AddBusinessButton onClick={() => setAdding(true)} /> : undefined}
    />
  )

  const columns: Column<Business>[] = [
    {
      id: 'business',
      header: t('label.business'),
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <Avatar name={row.name} />
          <span className="min-w-0">
            <span className="block truncate font-semibold text-dark">{row.name}</span>
            <span className="block truncate text-[11.5px] text-muted">
              {row.city} · {t('p.businesses.since', { when: sinceMonth(row.since) })}
            </span>
          </span>
        </span>
      ),
    },
    { id: 'owner', header: t('label.owner'), cell: (row) => row.ownerName },
    {
      id: 'plan',
      header: t('label.plan'),
      cell: (row) => <StatusBadge tone="accent">{planName(row.plan)}</StatusBadge>,
    },
    {
      id: 'animals',
      header: t('label.animals'),
      numeric: true,
      sortBy: (row) => row.animals,
      cell: (row) => <span className="tnum">{COUNT.format(row.animals)}</span>,
    },
    {
      id: 'bookValue',
      header: t('p.businesses.colBookValue'),
      numeric: true,
      sortBy: (row) => row.bookValuePaisa ?? -1,
      cell: (row) => <Money paisa={row.bookValuePaisa} short />,
    },
    {
      id: 'storage',
      header: t('label.storage'),
      numeric: true,
      secondary: true,
      sortBy: (row) => row.storageMb,
      cell: (row) => <span className="tnum">{formatBytes(row.storageMb)}</span>,
    },
    {
      id: 'status',
      header: t('label.status'),
      cell: (row) => {
        const status = statusOf(row)
        return <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      },
    },
    {
      id: 'pays',
      header: t('p.businesses.colPays'),
      numeric: true,
      // A trial is not charged, so there is no figure to hide — it is nothing.
      cell: (row) =>
        row.paysPaisa === undefined ? (
          <span className="text-light">{t('common.none')}</span>
        ) : (
          <Money paisa={row.paysPaisa} />
        ),
    },
  ]

  const accessLines: { id: string; icon: ReactNode; text: string }[] = [
    { id: '1', icon: <Check className="size-3.5" />, text: t('p.businesses.access1') },
    { id: '2', icon: <Check className="size-3.5" />, text: t('p.businesses.access2') },
    { id: '3', icon: <Check className="size-3.5" />, text: t('p.businesses.access3') },
    { id: '4', icon: <History className="size-3.5" />, text: t('p.businesses.access4') },
  ]

  return (
    <>
      <PageHeader
        title={t('p.businesses.title')}
        subtitle={t('p.businesses.subtitle', { count: total })}
        actions={
          <>
            <Button icon={<Download className="size-4" />} onClick={exportCsv}>
              {t('act.export')}
            </Button>
            {/* Absent for a support account, not disabled — P3 is
                PLATFORM_OWNER, and a greyed-out button still advertises it.
                The gate lives inside the button. */}
            <AddBusinessButton onClick={() => setAdding(true)} />
          </>
        }
      />

      <FilterBar>
        <SearchInput placeholder={t('p.businesses.searchPlaceholder')} />
        <FilterPills<StatusFilter>
          value={filter}
          onChange={setFilter}
          label={t('label.status')}
          pills={[
            { value: 'all', label: t('p.businesses.filterAll', { count: counts.all }) },
            { value: 'paying', label: t('p.businesses.filterPaying', { count: counts.paying }) },
            { value: 'trial', label: t('p.businesses.filterTrial', { count: counts.trial }) },
            {
              value: 'pastdue',
              label: t('p.businesses.filterPastDue', { count: counts.pastdue }),
            },
            {
              value: 'suspended',
              label: t('p.businesses.filterSuspended', { count: counts.suspended }),
            },
          ]}
        />
      </FilterBar>

      <Section>
        <QueryBoundary
          query={businesses}
          skeleton={<LoadingSkeleton variant="table" rows={6} />}
          isEmpty={(rows) => rows.length === 0}
          empty={emptyState}
        >
          {() => (
            <>
              <DataTable
                columns={columns}
                rows={shown}
                rowKey={(row) => row.id}
                caption={t('p.businesses.title')}
                onRowClick={(row) => navigate(routes.platform.business(row.slug))}
                rowActions={(row) => (
                  <RowAction
                    label={t('act.open')}
                    icon={<ChevronRight className="size-3.5" />}
                    onClick={() => navigate(routes.platform.business(row.slug))}
                  />
                )}
                empty={emptyState}
              />
              {shown.length > 0 ? <Pagination shown={shown.length} total={total} /> : null}
            </>
          )}
        </QueryBoundary>
      </Section>

      <Section
        surface="accent"
        className="mt-3.5"
        title={t('p.businesses.accessTitle')}
        action={<KeyRound className="size-[18px] text-accent-dark" />}
      >
        <ul className="flex flex-col gap-2">
          {accessLines.map((line) => (
            <li key={line.id} className="flex items-start gap-2.5 text-[13px] leading-snug text-sub">
              <span className="mt-px flex size-3.5 shrink-0 items-center justify-center text-accent-dark">
                {line.icon}
              </span>
              {line.text}
            </li>
          ))}
        </ul>
      </Section>

      <AddBusinessDialog open={adding} onClose={() => setAdding(false)} />
    </>
  )
}
